import React from "react";
import { DateField, RevqSelect, parseBuf, isoToDisplay } from "@/components/claim/ClaimLevelBoxes";
import "@/components/claim/claimform-v20.css";

import { INK, HAIR, MUTE as FAINT, INDIGO_TXT as LINK } from "@/lib/tokens";
const SOURCES = [["", "Any source"], ["document_upload", "Document upload"], ["superbill_import", "Superbill import"], ["pasted_note", "Pasted note"], ["dictation", "Dictation"], ["televisit", "Televisit"]];
const STATUSES = [["", "Any status"], ["ready", "Ready"], ["queued", "Queued"], ["processing", "Processing"], ["failed", "Failed"]];
const SIGS = [["", "Any"], ["true", "Signed"], ["false", "Not signed"]];
const CODES = [["", "Any"], ["stale", "Out of date"], ["fresh", "Up to date"]];
const TEXT = [["patient", "Patient name", "First or last name"], ["mrn", "MRN", "e.g. NTC-0001"], ["dob", "Date of birth"], ["visit_id", "Visit ID", "VIS-…"], ["cpt", "CPT", "e.g. 99213"], ["icd", "ICD-10", "e.g. E11.9"], ["provider", "Provider", "Name or NPI"], ["payer", "Payer", "Payer name"]];
export const ADV_LABEL = { patient: "Patient", mrn: "MRN", dob: "Date of birth", visit_id: "Visit ID", cpt: "CPT", icd: "ICD-10", provider: "Provider", payer: "Payer", dos_from: "DOS from", dos_to: "DOS to", source: "Source", status: "Status", tov: "Type of visit", signed: "Signature", codes: "Codes" };
const DATES = ["dob", "dos_from", "dos_to"];
const optLabel = { source: SOURCES, status: STATUSES, signed: SIGS, codes: CODES };

const filled = (d) => Object.entries(d).filter(([, v]) => v != null && String(v).trim());

export function advHints(d) {
  const h = {};
  const t = (k) => String(d[k] || "").trim();
  if (t("patient") && t("patient").split(/\s+/).some((w) => w.length < 3)) h.patient = "Each word needs 3+ letters";
  if (t("mrn") && t("mrn").length < 3) h.mrn = "3+ characters";
  if (t("visit_id") && !/^VIS-\S{4,}$/i.test(t("visit_id"))) h.visit_id = "VIS- plus 4+ characters";
  DATES.forEach((k) => { if (t(k) && !parseBuf(t(k)).valid) h[k] = "Use MM/DD/YYYY"; });
  const f = parseBuf(t("dos_from")).iso, to = parseBuf(t("dos_to")).iso;
  if (f && to && to < f) h.dos_to = "Must be on or after the from date";
  return h;
}
export const advReady = (d) => filled(d).length > 0 && Object.keys(advHints(d)).length === 0;

// Draft → POST /notes/search fields (dates as YYYY-MM-DD).
export function advBody(d) {
  const out = {};
  filled(d).forEach(([k, v]) => {
    const s = String(v).trim();
    if (DATES.includes(k)) out[k] = parseBuf(s).iso;
    else if (k === "signed") out.signed = s === "true";
    else if (k === "codes") out.codes_stale = s === "stale";
    else out[k] = k === "visit_id" ? s.toUpperCase() : s;
  });
  return out;
}
export const advChipList = (d) => filled(d).map(([k, v]) => ({ k, label: ADV_LABEL[k], value: optLabel[k] ? optLabel[k].find((o) => o[0] === v)?.[1] : String(v).trim() }));

const ADV_CSS = `.cc-ag{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px 14px;padding:14px 18px 16px}
.cc-ag .full{grid-column:span 2}
.cc-ag .cfv{display:block;height:auto;background:transparent}
@media (max-width:860px){.cc-ag{grid-template-columns:repeat(2,minmax(0,1fr))}.cc-ag .full{grid-column:1/-1}}
@media (max-width:620px){.cc-ag{grid-template-columns:1fr}.cc-ag .full{grid-column:auto}}`;

function F({ label, hint, k, children, cls }) {
  return (
    <div className={`min-w-0 ${cls || ""}`}>
      <label className="block text-[12px] font-semibold mb-1" style={{ color: "#6C6C69" }}>{label}</label>
      {children}
      {hint ? <span className="block text-[11.5px] mt-1" style={{ color: FAINT }} data-testid={`cc-adv-hint-${k}`}>{hint}</span> : null}
    </div>
  );
}

export function CCAdvancedSearch({ draft, setDraft, tovList, onSearch, onCancel }) {
  const hints = advHints(draft);
  const set = (k) => (v) => setDraft((s) => ({ ...s, [k]: v }));
  const date = (k, role, paired) => (
    <div className="cfv"><DateField buf={draft[k] || ""} onBuf={set(k)} iso={parseBuf(draft[k]).iso} pairedIso={paired} role={role}
      onPickIso={(iso) => set(k)(iso ? isoToDisplay(iso) : "")} testid={`cc-adv-${k}`} /></div>
  );
  const sel = (k, opts) => <div className="cfv"><RevqSelect value={draft[k] || ""} options={opts} onChange={set(k)} testid={`cc-adv-${k}`} /></div>;
  const tovOpts = [["", "Any type"], ...(tovList || []).map((t) => [t, t])];
  return (
    <>
      <style>{ADV_CSS}</style>
      <div className="cc-ag" data-testid="cc-adv-form">
        {TEXT.map(([k, label, ph]) => (
          <F key={k} k={k} label={label} hint={hints[k]}>
            {k === "dob" ? date("dob") : (
              <input value={draft[k] || ""} placeholder={ph} autoComplete="off" onChange={(e) => set(k)(e.target.value)}
                className="w-full text-[13.5px] px-[11px] h-[36px] rounded-[9px] outline-none bg-white focus:border-[#5856D6]" style={{ border: "1px solid #E2E2E6", color: INK }} data-testid={`cc-adv-${k}`} />
            )}
          </F>
        ))}
        <F k="dos" label="Date of service" hint={hints.dos_from || hints.dos_to} cls="full">
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">{date("dos_from", "from", parseBuf(draft.dos_to).iso)}</div>
            <span style={{ color: FAINT }}>–</span>
            <div className="flex-1 min-w-0">{date("dos_to", "to", parseBuf(draft.dos_from).iso)}</div>
          </div>
        </F>
        <F k="source" label="Source">{sel("source", SOURCES)}</F>
        <F k="status" label="Status">{sel("status", STATUSES)}</F>
        <F k="tov" label="Type of visit">{sel("tov", tovOpts)}</F>
        <F k="signed" label="Signature">{sel("signed", SIGS)}</F>
        <F k="codes" label="Codes">{sel("codes", CODES)}</F>
      </div>
      <div className="flex items-center gap-3 flex-wrap" style={{ borderTop: `1px solid ${HAIR}`, padding: "12px 18px" }}>
        <span className="text-[12.5px] max-w-[360px]" style={{ color: FAINT }}>Searches every encounter in the account, not just the loaded ones. Filled fields must all match.</span>
        <span className="flex-1" />
        <button onClick={() => setDraft({})} className="text-[13px] font-semibold" style={{ color: LINK }} data-testid="cc-adv-clear">Clear</button>
        <button onClick={onCancel} className="text-[13px] font-semibold rounded-[9px] px-4 h-[36px] bg-white" style={{ border: `1px solid ${HAIR}`, color: INK }} data-testid="cc-adv-cancel">Cancel</button>
        <button onClick={onSearch} disabled={!advReady(draft)} className="text-[13px] font-bold text-white rounded-[9px] px-4 h-[36px] disabled:opacity-35" style={{ background: INK }} data-testid="cc-adv-search">Search</button>
      </div>
    </>
  );
}
